import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { ServiceType } from '@/types/scheduler';

/** The skill (ServiceType) create/edit form shape — shared by the Settings →
 *  Skills page and the inline SkillCreateDialog. */
export interface SkillFormValues {
  name: string;
  slug: string;
  tradeId: string | null;
  isCertification: boolean;
  defaultValidityMonths: number | null;
}

export const EMPTY_SKILL_FORM: SkillFormValues = {
  name: '',
  slug: '',
  tradeId: null,
  isCertification: false,
  defaultValidityMonths: null,
};

export function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Create a skill (service type); refetches the list + toasts. */
export function useCreateSkill() {
  const queryClient = useQueryClient();
  return useApiMutation<ServiceType, SkillFormValues>({
    mutationFn: (dto) =>
      apiPost<ServiceType, SkillFormValues>(
        operationsClient,
        schedulerEndpoints.serviceTypes(),
        dto,
      ),
    showErrorToast: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.serviceTypes() });
      toast.success('Skill created');
    },
    onError: (error) => {
      if (error.status === 422) {
        toast.error('A skill with this slug already exists. Choose a different name or slug.');
      } else {
        toast.error(error.message);
      }
    },
  });
}
